import { corsImport, getLabel } from '@weapp/utils';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import uniq from 'lodash-es/uniq';
import { EntranceDataType, TerminalType } from '../../common/types';
import { LayoutType, PageModuleType } from '../../constants';
import { PageLayoutType } from '../../constants/pc';
import ebdcoms from '../../utils/ebdcoms';
import { dftPageStyle, MDftPageStyle } from '../../utils/page-style';
import { isExactContainer, isFloatCom, isFlowCom } from './grid-layout/utils';
import {
  Comp, IContainerItem, IContainerManager, Page, PageInfo,
} from './types';
/**
 * 页面的外边距
 */
const dftPageMargin = {
  selectedId: 'default',
  space: '',
};

/**
 * 分栏的背景色
 */
const dftBackground = {
  backgroundColor: '',
  backgroundImage: '',
  backgroundPosition: 'center center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'auto',
  positionType: 'grid',
  imgUrl: '',
};

const dftPageOpt = (layoutType: LayoutType) => ({
  margin: dftPageMargin,
  padding: dftPageStyle[layoutType].padding,
  background: dftBackground,
});

const getDftPageOpt = (page: Page, layoutType: LayoutType) => {
  if (layoutType === LayoutType.GridLayout && page.terminalType === 'MOBILE') {
    return {
      padding: MDftPageStyle[layoutType].padding,
    };
  }
  return dftPageOpt(layoutType);
};

// 获取页面样式
export const getPageStyles = (page: Page, layoutType: LayoutType) => {
  let pageStyles = {
    ...page.config.style?.page,
  };
  const themeStyleConfig = page.themeData?.styleConfig; // 主题样式
  if (
    (!!themeStyleConfig && isEqual(page.config.style?.page, getDftPageOpt(page, layoutType)))
    || page.config.style?.useCustom === false
    || isEmpty(page.config.style?.page)
    || (Object.keys(page.config.style.page || {}).length === 1
      && (isEqual(page.config.style.page.background, dftBackground)
        || isEqual(page.config.style.page.padding, dftPageStyle[layoutType].padding)))
  ) {
    pageStyles = { ...themeStyleConfig };
  } else if (
    page.config.style?.useCustom
    || (page.config.style?.useCustom === undefined && !isEmpty(page.config.style?.page))
  ) {
    pageStyles = {
      ...page.config.style?.page,
    };
  }
  return pageStyles;
};

// 判断是对象，并且对象中有值
const checkObject = (data: any) => Object.prototype.toString.call(data) === '[object Object]' && Object.keys(data).length > 0;

// 为了兼容老数据的方法
export const initLinkedNodes = (com: any) => {
  const linkedNodes: any = {};
  if (com.type === 'GridMultiColumn') {
    if (com.config.flow?.nodes?.length && !com.config?.flow?.linkedNodes) {
      com.config.flow?.nodes.forEach((id: string, index: number) => {
        linkedNodes[`${index}`] = id;
      });
    }
  }
  if (com.type === 'NewTab') {
    if (com.config?.tabs?.length && !com.config?.flow?.linkedNodes) {
      com.config.tabs.forEach((tab: any) => {
        linkedNodes[tab.id] = tab?.droppableId || '';
      });
    }
  }
  if (com.type === 'EbTableLayout') {
    if (com.config.flow?.nodes?.length && !com.config?.flow?.linkedNodes) {
      Object.keys(com.config.cellMergeInfo || {}).forEach((key) => {
        linkedNodes[key] = com.config.cellMergeInfo[key].droppableId || '';
      });
    }
  }

  if (checkObject(linkedNodes)) {
    com.config.flow.linkedNodes = {
      ...linkedNodes,
    };
  }
  return com;
};

/**
 * 获取延迟渲染的布局范围
 * @param page
 */
export function getLayoutScopesWithLazyRender(page: Page) {
  const { config } = page || {};
  const {
    leftSider, rightSider, selectMain, shrink,
  } = config || {};
  let scopes: string[] = [];

  switch (config?.pageLayout) {
    case PageLayoutType.LeftSide:
    case PageLayoutType.TopLeftRight:
      scopes = leftSider.shrink?.delayRender ? ['leftSider'] : [];
      break;
    case PageLayoutType.RightSide:
      scopes = rightSider.shrink?.delayRender ? ['rightSider'] : [];
      break;
    case PageLayoutType.LeftCenterRight:
      if (selectMain !== 'leftSider' && leftSider.shrink?.delayRender) {
        scopes.push('leftSider');
      }

      if (selectMain !== '' && shrink?.delayRender) {
        scopes.push('');
      }

      if (selectMain !== 'rightSider' && rightSider.shrink?.delayRender) {
        scopes.push('rightSider');
      }

      break;
    default:
      break;
  }

  return scopes;
}

export function initSideCompRender(comp: Comp, lazyLayoutScopes: string[]) {
  if (lazyLayoutScopes.includes(comp.config.layout.scope!)) {
    comp.config.layout.isRenderAfterExpandSide = true;
  }
}

/**
 * 获取组件的子组件
 */
export const getChildComs = (com: Comp, coms: Comp[]): Comp[] => {
  const { nodes = [], linkedNodes = {} } = com?.config?.flow || {};
  const childComIds = uniq([...nodes, ...Object.values(linkedNodes)]);

  return childComIds
    .map((id) => {
      const childCom = coms.find((c) => c.id === id) as Comp;
      if (!childCom) {
        return childCom;
      }
      return {
        ...childCom,
        childComs: getChildComs(childCom, coms),
      };
    })
    .filter((childCom) => childCom);
};

const filterVisibleComs = (coms: Comp[]) => {
  const _coms = [...coms];
  const visibleComs: Comp[] = [];
  const verifiedComs: Comp[] = [];
  const remainComs: Comp[] = [];
  _coms.forEach((com) => {
    if (isFloatCom(com)) return;
    if (!isFlowCom(com)) {
      verifiedComs.push(com);
    } else {
      remainComs.push(com);
    }
  });
  for (let i = 0; i < verifiedComs.length; i++) {
    const item = verifiedComs[i];
    if (item && !item.config.layout.hidden) {
      visibleComs.push(item);
      const nodes = item.config?.flow?.nodes;
      if (nodes?.length) {
        const items = nodes.map((nodeId) => {
          const index = remainComs.findIndex((_com) => _com.id === nodeId);
          const node = remainComs.splice(index, 1)[0];
          return node;
        });
        verifiedComs.push(...items);
      }
    }
  }
  return visibleComs;
};

export const resourcePreloader = {
  preloadPage: (page: any) => {
    if (!page) return;

    corsImport('@weapp/designer').then((module: any) => {
      module.preloadPage?.({ layoutType: page.layoutType, clientType: page.terminalType });
    });
  },
  preloadComs: (coms: Comp[], terminalType: TerminalType) => {
    if (!coms || !coms.length) return;
    const visibleComs = filterVisibleComs(coms);
    const packageMaps = visibleComs.reduce((map, com) => {
      if (!com.package || !com.type) return map;
      if (!map[com.package]) {
        map[com.package] = {
          [com.type]: true,
        };
      } else if (!map[com.package][com.type]) {
        map[com.package][com.type] = true;
      }
      return map;
    }, {} as Record<string, any>);
    // 【性能优化】提前加载coms_view资源，避免ComView中等待资源阻塞渲染
    corsImport('@weapp/ebdcoms').then(({ getView }: any) => getView(''));
    Object.keys(packageMaps).forEach((pkgName: string) => {
      corsImport(pkgName).then((module: any) => {
        const comTypes = Object.keys(packageMaps[pkgName]);
        // 目前只有ebdcoms提供preloadComs，其他组件库可以直接拿到loader
        if (pkgName === '@weapp/ebdcoms') {
          module?.getUtils?.()?.then(({ preloadComs }: any) => {
            preloadComs?.(comTypes, terminalType);
          });
        } else {
          const ebcoms = module?.ebcoms;
          const loader = terminalType === 'MOBILE' ? 'MView' : 'View';
          comTypes.forEach((type) => ebcoms?.[type]?.[loader]?.());
        }
      });
    });
  },
};

const viewInfos: any = {};

const getViewInfo = (com: Comp, client?: string) => ebdcoms.load().then((mod) => {
  const getInfo = client === 'MOBILE' ? mod.getMView : mod.getView;
  return getInfo(com.type, com.package);
});

export const initViewInfos = (coms: Comp[], client?: string) => {
  coms.forEach((com) => {
    if (!viewInfos[com.type]) {
      viewInfos[com.type] = { loaded: false };
      getViewInfo(com, client).then((info) => {
        viewInfos[com.type].loaded = true;
        viewInfos[com.type].info = info;
      });
    }
  });
};

/**
 * 判断容器里面是否有子组件
 */
export const isEmptyContainer = (com: Comp) => com.config.layout?.isEmpty;

export const getShouldLoadComs = (coms: Comp[]) => coms.filter((el) => {
  const parent = el.config?.flow?.parent;
  const hidden = el.config.layout?.hidden;
  const isRenderAfterExpandSide = el.config.layout?.isRenderAfterExpandSide;

  return !el.pid
    && (!parent || parent === 'ROOT')
    && !hidden
    && !isRenderAfterExpandSide
    && !isEmptyContainer(el);
});

/** 判断页面中一级组件是否全部加载完成 */
export const getCompsLoaded = (coms: Comp[], loadedComs: string[], isVerified: boolean) => {
  if (!coms.length) {
    return !!isVerified; /** 页面本身没有组件 */
  }
  // 只有一级组件需要监听是否全部加载，嵌套组件不监听
  const shouldLoadComs = getShouldLoadComs(coms);
  const unloadedCom = shouldLoadComs.find((com) => !loadedComs.includes(com.id));
  return !unloadedCom;
};

export const setHideForParentComs = (containerManager: IContainerManager) => {
  const setHide = (info: IContainerItem, comId: string) => {
    const validChild = info.childList.find((childId) => !info.hideList.includes(childId));
    // 确保子元素中存在不隐藏的元素，如果不存在则进行以下逻辑，兼容脏数据引发的页面空白场景
    if (!validChild) {
      const parentId = info.parent;
      const containerInfo = containerManager[parentId];
      // 选项卡内元素不做隐藏
      // 存在容器组件comRef为null
      if (info.comRef && !(info.comRef.type === 'InternalContainer' && !containerInfo)) {
        info.comRef.config.layout.isEmpty = true;
      }
      if (
        containerInfo
        && containerInfo.comRef
        && isExactContainer(containerInfo.comRef)
        && !containerInfo.hideList?.includes(comId)
      ) {
        containerInfo.hideList.push(comId);
        setHide(containerInfo, parentId);
      }
    }
  };
  Object.keys(containerManager).forEach((comId) => {
    const containerInfo = containerManager[comId];
    setHide(containerInfo, comId);
  });
};
export const isBILayout = (type?: PageModuleType) => type === PageModuleType.BI;

/**
 * 针对状态码401和msg两个字段获取对应的展示文案
 * @param responseMsg 响应的msg
 * @returns 提示文案
 */
export const getNoPermissionErrorInfo = (responseMsg: string): string => {
  switch (responseMsg) {
    case '0':
      return getLabel('67394', '对不起！您暂时没有权限！');
    case '8':
      return getLabel('241188', '非常抱歉，您的团队目前是免费版本，不支持此功能！');
    default:
      return getLabel('91812', '没有相关权限');
  }
};

/**
 * 判断是否需要显示ErrorInfo错误信息
 */
export const getHasErrorInfo = (res: any) => {
  let hasErrorInfo = false;

  if (!res) {
    return hasErrorInfo;
  }

  if (res.code === 401) {
    hasErrorInfo = !!getNoPermissionErrorInfo(res.msg);

    if (hasErrorInfo) return hasErrorInfo;
  }

  if (!res.data) {
    hasErrorInfo = !!res?.msg;

    if (hasErrorInfo) return hasErrorInfo;
  }

  if (!res.status && res.msg) {
    hasErrorInfo = !!res.msg;

    if (hasErrorInfo) return hasErrorInfo;
  }

  return hasErrorInfo;
};

/**
 * 取出接口里返回的数据
 * @param data 接口返回页面数据
 */
export const getEntranceData = (data: EntranceDataType<PageInfo>): EntranceDataType<Page> => {
  const { page, ...restData } = data || {};
  /**
   * 为兼容 page 下 appid 为驼峰形式的情况
   */
  const appId = page?.appid || page?.appId;

  return {
    ...restData,
    page: {
      ...page,
      appid: appId!,
    },
  };
};

export default {};
